import {
  detectAnomalies,
  detectSameMonthAnomalies,
  saveHistoricalData,
  clearOpenAnomaliesForPeriods,
  getHistorySpan,
  getSameMonthHistory,
  getCompanyId,
  isElectricityMetric,
  isInsufficientHistory,
  normalizeSameMonthAnomaly,
  PURCHASED_ELECTRICITY_METRIC_KEY,
} from "./anomalyApi";

const METRIC_KEY = PURCHASED_ELECTRICITY_METRIC_KEY;

// Month names for human-readable messages
const MONTH_NAMES = ['', 'January', 'February', 'March', 'April', 'May', 'June',
                     'July', 'August', 'September', 'October', 'November', 'December'];

// Minimum months of history required for same-month baseline detection
const MIN_HISTORY_MONTHS = 13;
const YEAR_TOTALS_KEY = "ecoprism_anomaly_yearly_totals";
const QUARTER_TOTALS_KEY = "ecoprism_anomaly_quarterly_totals";

const normalizeKey = (key) =>
  String(key || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

/** Keep only rows for this site location (never mix sites). */
export function filterRowsForSite(rows, siteLocationId) {
  if (!Array.isArray(rows) || !siteLocationId) return rows || [];
  const site = String(siteLocationId).trim().toLowerCase();
  return rows.filter((row) => {
    const siteKey = findColumnKey(row, [
      "sitelocation",
      "sitelocationid",
      "site location",
      "site",
    ]);
    if (!siteKey) return true;
    return String(row[siteKey] || "").trim().toLowerCase() === site;
  });
}

function findColumnKey(row, matchers) {
  if (!row || typeof row !== "object") return null;
  for (const key of Object.keys(row)) {
    const nk = normalizeKey(key);
    if (matchers.some((m) => nk.includes(m))) return key;
  }
  return null;
}

function parseExcelDate(value) {
  if (value == null || value === "") return null;
  if (typeof value === "number" && value > 30000) {
    const MS_PER_DAY = 86400000;
    const EPOCH = 25569;
    return new Date((value - EPOCH) * MS_PER_DAY);
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  const s = String(value).trim();
  const slash = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (slash) {
    const [, m, d, y] = slash;
    return new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
  }
  const parsed = new Date(s);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function parseEnergyValue(value) {
  if (value == null || value === "") return null;
  const num = parseFloat(String(value).replace(/,/g, ""));
  return Number.isNaN(num) ? null : num;
}

function getRowEnergy(row) {
  const key =
    findColumnKey(row, ["electricityconsumption", "energyconsumption", "purchasedelectricity"]) ||
    findColumnKey(row, ["electricityconsumptionmwh", "electricitymwh"]) ||
    findColumnKey(row, ["electricity", "energycons", "energy"]);
  if (!key) return null;
  return parseEnergyValue(row[key]);
}

function getRowPeriod(row) {
  const endKey = findColumnKey(row, ["enddate", "end"]);
  const startKey = findColumnKey(row, ["startdate", "start"]);
  const d = parseExcelDate(row[endKey]) || parseExcelDate(row[startKey]);
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

/**
 * Calculate billing days from start and end dates in a row.
 * Falls back to 30 if dates not available or invalid.
 */
function getRowBillingDays(row) {
  const startKey = findColumnKey(row, ["startdate", "start"]);
  const endKey = findColumnKey(row, ["enddate", "end"]);
  const startDate = parseExcelDate(row[startKey]);
  const endDate = parseExcelDate(row[endKey]);

  if (startDate && endDate && endDate >= startDate) {
    // Inclusive billing days: Jan 1–Jan 31 = 31 days, not 30
    const startUtc = Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const endUtc = Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
    const diffDays = Math.round((endUtc - startUtc) / (1000 * 60 * 60 * 24)) + 1;
    return Math.max(1, Math.min(diffDays, 365));
  }
  return 30; // Default billing days
}

/**
 * Extract year and month from period string (YYYY-MM).
 */
function parsePeriod(period) {
  if (!period || !period.includes("-")) return null;
  const [year, month] = period.split("-");
  return {
    year: parseInt(year, 10),
    month: parseInt(month, 10),
    period,
  };
}

/**
 * Group periods by calendar month for same-month baseline detection.
 * Returns a map: { 1: [...January periods...], 2: [...February periods...], ... }
 */
function groupPeriodsByCalendarMonth(periods) {
  const groups = {};
  for (let m = 1; m <= 12; m++) {
    groups[m] = [];
  }

  for (const p of periods) {
    const parsed = parsePeriod(p.period);
    if (!parsed) continue;
    groups[parsed.month].push({
      ...p,
      year: parsed.year,
      month: parsed.month,
    });
  }

  // Sort each group by year (most recent last)
  for (const m of Object.keys(groups)) {
    groups[m].sort((a, b) => a.year - b.year);
  }

  return groups;
}

function periodToQuarter(period) {
  if (!period || !period.includes("-")) return null;
  const [y, m] = period.split("-");
  const month = parseInt(m, 10);
  if (Number.isNaN(month)) return null;
  const q = Math.ceil(month / 3);
  return `${y}-Q${q}`;
}

/** Aggregate kWh by calendar quarter (YYYY-Qn), sorted chronologically */
export function aggregateQuartersFromExcelRows(rows) {
  const periods = aggregatePeriodsFromExcelRows(rows);
  const buckets = {};
  for (const { period, value } of periods) {
    const q = periodToQuarter(period);
    if (!q) continue;
    buckets[q] = (buckets[q] || 0) + value;
  }
  return Object.entries(buckets)
    .map(([quarter, value]) => ({ quarter, value }))
    .sort((a, b) => a.quarter.localeCompare(b.quarter));
}

/** Sum consumption by calendar year from row dates */
export function aggregateYearsFromRows(rows) {
  const periods = aggregatePeriodsFromExcelRows(rows);
  const buckets = {};
  for (const { period, value } of periods) {
    const y = period.split("-")[0];
    if (!y) continue;
    buckets[y] = (buckets[y] || 0) + value;
  }
  return Object.entries(buckets)
    .map(([year, value]) => ({ year, value }))
    .sort((a, b) => a.year.localeCompare(b.year));
}

/** Years present in report/history rows (for filters) */
export function yearsFromReportRows(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const years = new Set();
  for (const row of rows) {
    const endKey = findColumnKey(row, ["enddate", "end"]);
    const startKey = findColumnKey(row, ["startdate", "start"]);
    const d = parseExcelDate(row[endKey]) || parseExcelDate(row[startKey]);
    if (d) years.add(String(d.getFullYear()));
  }
  return [...years].sort((a, b) => b.localeCompare(a));
}

/** Aggregate kWh by YYYY-MM period, sorted chronologically. Includes billing days. */
export function aggregatePeriodsFromExcelRows(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  const buckets = {};
  const daysBuckets = {};

  for (const row of rows) {
    const energy = getRowEnergy(row);
    const period = getRowPeriod(row);
    const days = getRowBillingDays(row);
    if (energy == null || !period) continue;
    buckets[period] = (buckets[period] || 0) + energy;
    // For billing days, take the max if multiple rows per period
    daysBuckets[period] = Math.max(daysBuckets[period] || 0, days);
  }

  return Object.entries(buckets)
    .map(([period, value]) => ({
      period,
      value,
      billingDays: daysBuckets[period] || 30,
    }))
    .sort((a, b) => a.period.localeCompare(b.period));
}

export function getAnnualTotalFromRows(rows) {
  return rows.reduce((sum, row) => {
    const v = getRowEnergy(row);
    return v != null ? sum + v : sum;
  }, 0);
}

export function inferYearFromFileName(fileName) {
  const m = String(fileName || "").match(/20\d{2}/);
  return m ? m[0] : null;
}

export function inferYearFromRows(rows) {
  let maxYear = null;
  for (const row of rows) {
    const endKey = findColumnKey(row, ["enddate", "end"]);
    const startKey = findColumnKey(row, ["startdate", "start"]);
    const d = parseExcelDate(row[endKey]) || parseExcelDate(row[startKey]);
    if (d) {
      const y = d.getFullYear();
      if (!maxYear || y > maxYear) maxYear = y;
    }
  }
  return maxYear ? String(maxYear) : null;
}

function totalsStorageKey(customerId, facility) {
  return `${customerId}|${facility}`;
}

function loadYearTotals(customerId, facility) {
  try {
    const all = JSON.parse(localStorage.getItem(YEAR_TOTALS_KEY) || "{}");
    return all[totalsStorageKey(customerId, facility)] || {};
  } catch {
    return {};
  }
}

function saveYearTotal(customerId, facility, year, total) {
  const all = JSON.parse(localStorage.getItem(YEAR_TOTALS_KEY) || "{}");
  const key = totalsStorageKey(customerId, facility);
  all[key] = { ...(all[key] || {}), [year]: total };
  localStorage.setItem(YEAR_TOTALS_KEY, JSON.stringify(all));
}

function loadQuarterTotals(customerId, facility) {
  try {
    const all = JSON.parse(localStorage.getItem(QUARTER_TOTALS_KEY) || "{}");
    return all[totalsStorageKey(customerId, facility)] || {};
  } catch {
    return {};
  }
}

function saveQuarterTotal(customerId, facility, quarter, total) {
  const all = JSON.parse(localStorage.getItem(QUARTER_TOTALS_KEY) || "{}");
  const key = totalsStorageKey(customerId, facility);
  all[key] = { ...(all[key] || {}), [quarter]: total };
  localStorage.setItem(QUARTER_TOTALS_KEY, JSON.stringify(all));
}

function previousYearQuarter(quarter) {
  const m = String(quarter || "").match(/^(20\d{2})-Q([1-4])$/);
  if (!m) return null;
  return `${parseInt(m[1], 10) - 1}-Q${m[2]}`;
}

function notifyAnomalyUpdated() {
  window.dispatchEvent(new CustomEvent("anomaly-data-updated"));
}

async function runSingleDetect({ companyId, siteLocation, billingPeriod, current, previous, label }) {
  try {
    const result = await detectAnomalies({
      companyId,
      siteLocation,
      esgMetricKey: METRIC_KEY,
      billingPeriod: billingPeriod || new Date().toISOString().slice(0, 7),
      metrics: { [METRIC_KEY]: current },
      previousMetrics: previous != null ? { [METRIC_KEY]: previous } : undefined,
    });
    return {
      label,
      period: billingPeriod,
      detected: result?.anomaly_detected === true,
      count: result?.totalAnomalies || 0,
      result,
    };
  } catch (err) {
    console.error("Anomaly detect failed:", label, err);
    return { label, period: billingPeriod, detected: false, count: 0, error: err.message };
  }
}

/**
 * Run same-month baseline detection for a single month.
 * Compares current month value against historical same-month values.
 */
async function runSameMonthDetect({
  companyId,
  siteLocation,
  billingPeriod,
  currentValueKwh,
  billingDays,
  historicalSameMonth,
  monthNumber,
}) {
  const monthName = MONTH_NAMES[monthNumber] || `Month ${monthNumber}`;

  if (!historicalSameMonth || historicalSameMonth.length === 0) {
    return {
      label: `${monthName} (first occurrence)`,
      period: billingPeriod,
      detected: false,
      count: 0,
      skipped: true,
      reason: "first_occurrence",
    };
  }

  try {
    const result = await detectSameMonthAnomalies({
      companyId,
      siteLocation,
      billingPeriod,
      currentValueKwh,
      billingDays,
      historicalSameMonth: historicalSameMonth.map((h) => ({
        valueKwh: h.valueKwh ?? h.value,
        billingDays: h.billingDays ?? h.days ?? 30,
        year: h.year,
      })),
    });

    if (isInsufficientHistory(result)) {
      return {
        label: `${monthName} (insufficient history)`,
        period: billingPeriod,
        detected: false,
        count: 0,
        skipped: true,
        reason: "insufficient_history",
        historicalCount: historicalSameMonth.length,
      };
    }

    const anomalies = (result?.anomalies || []).map(normalizeSameMonthAnomaly);
    const detected = result?.anomaly_detected === true;

    return {
      label: `${monthName} baseline check`,
      period: billingPeriod,
      detected,
      count: result?.totalAnomalies || 0,
      result,
      anomalies,
      expectedRange: result?.expectedRange,
      median: result?.median,
      historicalCount: result?.historicalCount || historicalSameMonth.length,
    };
  } catch (err) {
    console.error("Same-month detect failed:", billingPeriod, err);
    return {
      label: `${monthName} baseline check`,
      period: billingPeriod,
      detected: false,
      count: 0,
      error: err.message,
    };
  }
}

/**
 * Save all period data to history for future baseline building.
 */
/**
 * Merge in-file prior years with Azure-stored same-month history.
 * Stored history is used when uploading one year at a time (e.g. 2025 after 2022–2024).
 */
async function buildHistoricalSameMonth({
  companyId,
  siteLocation,
  monthNumber,
  maxYear,
  inFileEntries,
}) {
  const byYear = new Map();

  for (const entry of inFileEntries) {
    if (entry.year < maxYear) {
      byYear.set(entry.year, entry);
    }
  }

  try {
    const stored = await getSameMonthHistory(companyId, siteLocation, monthNumber, METRIC_KEY);
    for (const row of stored?.history || []) {
      const year = row.year;
      if (!year || year >= maxYear) continue;
      if (!byYear.has(year)) {
        byYear.set(year, {
          value: row.valueKwh ?? row.value,
          days: row.billingDays || 30,
          year,
        });
      }
    }
  } catch (err) {
    console.warn("Could not load stored same-month history:", monthNumber, err);
  }

  return [...byYear.values()].sort((a, b) => a.year - b.year);
}

async function saveAllPeriodsToHistory({ companyId, siteLocation, periods }) {
  const results = [];
  for (const p of periods) {
    try {
      await saveHistoricalData({
        companyId,
        siteLocation,
        billingPeriod: p.period,
        valueKwh: p.value,
        billingDays: p.billingDays || 30,
      });
      results.push({ period: p.period, saved: true });
    } catch (err) {
      console.error("Failed to save history:", p.period, err);
      results.push({ period: p.period, saved: false, error: err.message });
    }
  }
  return results;
}

/**
 * After successful Excel RunReport: same-month baseline detection only.
 *
 * Same-month approach (fixes seasonal false positives):
 * - Compare June 2025 to June 2024, June 2023, etc. (not May 2025)
 * - Use median + IQR for robust outlier detection
 * - Save all periods to history for future baseline building
 *
 * Year-over-year / period-over-period detection is disabled for now.
 */
export async function runAnomalyDetectionFromExcelRows({
  rows,
  facility,
  esgMetric,
  fileName,
  customerId: customerIdParam,
}) {
  if (!isElectricityMetric(esgMetric)) {
    return { skipped: true, reason: "not_electricity", totalAnomalies: 0, checks: [] };
  }

  const companyId = customerIdParam || getCompanyId();
  const siteLocation = facility;
  if (!companyId || !siteLocation) {
    return { skipped: true, reason: "missing_ids", totalAnomalies: 0, checks: [] };
  }

  const siteRows = filterRowsForSite(rows, siteLocation);
  if (siteRows.length === 0) {
    return { skipped: true, reason: "no_rows_for_site", totalAnomalies: 0, checks: [] };
  }

  const checks = [];
  let totalAnomalies = 0;
  let insufficientHistory = false;
  let monthsAvailable = 0;

  const periods = aggregatePeriodsFromExcelRows(siteRows);

  // Clear stale open anomalies for these periods before re-scanning
  const yearsInUpload = [...new Set(periods.map((p) => p.period.split("-")[0]).filter(Boolean))];
  const periodsToClear = [
    ...periods.map((p) => p.period),
    ...yearsInUpload.map((y) => `${y}-12`),
  ];
  try {
    await clearOpenAnomaliesForPeriods({ companyId, siteLocation, billingPeriods: periodsToClear });
  } catch (err) {
    console.warn("Could not clear prior anomalies for periods:", err);
  }

  // Step 1: Save all periods to history for future baseline building
  await saveAllPeriodsToHistory({ companyId, siteLocation, periods });

  // Step 2: Check history span
  try {
    const historySpan = await getHistorySpan(companyId, siteLocation, METRIC_KEY);
    monthsAvailable = historySpan?.monthsAvailable || periods.length;
    insufficientHistory = monthsAvailable < MIN_HISTORY_MONTHS;
  } catch (err) {
    console.warn("Could not check history span:", err);
    monthsAvailable = periods.length;
    insufficientHistory = monthsAvailable < MIN_HISTORY_MONTHS;
  }

  // Step 3: Same-month baseline detection (replaces period-over-period)
  if (!insufficientHistory && periods.length > 0) {
    // Group by calendar month
    const monthGroups = groupPeriodsByCalendarMonth(periods);

    // Find the most recent year in data
    const maxYear = Math.max(...periods.map((p) => {
      const parsed = parsePeriod(p.period);
      return parsed ? parsed.year : 0;
    }));

    // For each month with current year data, compare against prior years
    for (let month = 1; month <= 12; month++) {
      const monthData = monthGroups[month];
      if (monthData.length === 0) continue;

      // Find current year entry (most recent year in data)
      const currentYearEntry = monthData.find((m) => m.year === maxYear);
      if (!currentYearEntry) continue;

      const inFileEntries = monthData
        .filter((m) => m.year < maxYear)
        .map((m) => ({
          value: m.value,
          days: m.billingDays || 30,
          year: m.year,
        }));

      const historicalEntries = await buildHistoricalSameMonth({
        companyId,
        siteLocation,
        monthNumber: month,
        maxYear,
        inFileEntries,
      });

      const out = await runSameMonthDetect({
        companyId,
        siteLocation,
        billingPeriod: currentYearEntry.period,
        currentValueKwh: currentYearEntry.value,
        billingDays: currentYearEntry.billingDays || 30,
        historicalSameMonth: historicalEntries,
        monthNumber: month,
      });

      checks.push(out);
      if (out.detected) totalAnomalies += out.count;
    }
  }

  // Year-over-year / period-over-period detection disabled — same-month baseline only.
  const yearBuckets = aggregateYearsFromRows(siteRows);
  const year =
    inferYearFromFileName(fileName) || inferYearFromRows(siteRows) || new Date().getFullYear().toString();
  const annualTotal = getAnnualTotalFromRows(siteRows);

  if (checks.length > 0) {
    notifyAnomalyUpdated();
  }

  return {
    skipped: false,
    totalAnomalies,
    checks,
    periodsFound: periods.length,
    year,
    annualTotal,
    yearsInData: yearBuckets.map((y) => y.year),
    rowsUsed: siteRows.length,
    // New fields for same-month baseline
    insufficientHistory,
    monthsAvailable,
    monthsRequired: MIN_HISTORY_MONTHS,
    detectionMethod: insufficientHistory ? "none" : "same_month_baseline",
  };
}

/** Scan rows already loaded on Report History (same logic as Excel upload). */
export function runAnomalyDetectionFromReportHistoryRows(args) {
  return runAnomalyDetectionFromExcelRows({ ...args, fileName: args.fileName || "report-history" });
}

export function formatAnomalySummary(summary, { facility, esgMetric } = {}) {
  if (!summary || summary.skipped) return "";

  const detailsUrl =
    facility && esgMetric
      ? `/site-anomalies?sl=${encodeURIComponent(facility)}&esg=${encodeURIComponent(esgMetric)}`
      : null;

  const detailsLink = detailsUrl
    ? `<p style="margin-top:10px;"><a href="${detailsUrl}" style="color:#407775;font-weight:600;font-size:14px;">View anomaly details →</a></p>`
    : "";

  // Show insufficient history message if applicable
  if (summary.insufficientHistory) {
    return `<p style="font-size:14px;margin-top:10px;color:#666;">
      <strong>Anomaly detection:</strong> Insufficient history for baseline detection.
      <br/><span style="font-size:12px;">Currently have ${summary.monthsAvailable || 0} month(s); ${summary.monthsRequired || 13} months required for same-month baseline detection.</span>
    </p>`;
  }

  if (summary.totalAnomalies === 0) {
    const methodNote = summary.detectionMethod === "same_month_baseline"
      ? " (same-month baseline)"
      : "";
    return `<p style="font-size:14px;margin-top:10px;color:#407775;">Anomaly scan${methodNote}: no issues flagged (${summary.periodsFound || 0} period(s) analyzed).</p>`;
  }

  const methodNote = summary.detectionMethod === "same_month_baseline"
    ? " (same-month baseline)"
    : "";
  const countLabel = summary.totalAnomalies === 1 ? "anomaly" : "anomalies";

  return `<p style="font-size:14px;margin-top:10px;color:#c62828;"><strong>${summary.totalAnomalies} ${countLabel} detected${methodNote}</strong></p>${detailsLink}`;
}
